"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

type StackItem = {
  name: string;
  description: string;
  descriptionKey?: string;
  iconUrl: string;
};

const stacks: { titleKey: string; items: StackItem[] }[] = [
  {
    titleKey: "techStack.frontend",
    items: [
      {
        name: "Next.js",
        description: "SSR/SSG, routing, edge-ready",
        descriptionKey: "techStack.items.nextjs",
        iconUrl: "https://cdn.simpleicons.org/nextdotjs/000000",
      },
      {
        name: "React",
        description: "Component architectures",
        descriptionKey: "techStack.items.react",
        iconUrl: "https://cdn.simpleicons.org/react/61DAFB",
      },
      {
        name: "TypeScript",
        description: "Typed UI & state",
        descriptionKey: "techStack.items.typescript",
        iconUrl: "https://cdn.simpleicons.org/typescript/3178C6",
      },
      {
        name: "Framer Motion",
        description: "Micro-interactions",
        descriptionKey: "techStack.items.framer",
        iconUrl: "https://cdn.simpleicons.org/framer/0055FF",
      },
    ],
  },
  {
    titleKey: "techStack.mobile",
    items: [
      {
        name: "React Native",
        description: "Cross-platform mobile apps",
        descriptionKey: "techStack.items.reactNative",
        iconUrl: "https://cdn.simpleicons.org/react/61DAFB",
      },
      {
        name: "Firebase Cloud Messaging",
        description: "Push notifications",
        descriptionKey: "techStack.items.fcm",
        iconUrl: "https://cdn.simpleicons.org/firebase/FFCA28",
      },
      {
        name: "App Store Deploy",
        description: "iOS builds & submission",
        descriptionKey: "techStack.items.appStore",
        iconUrl: "https://cdn.simpleicons.org/apple/000000",
      },
      {
        name: "Google Play Deploy",
        description: "Android builds & release",
        descriptionKey: "techStack.items.googlePlay",
        iconUrl: "https://cdn.simpleicons.org/googleplay/414141",
      },
    ],
  },
  {
    titleKey: "techStack.apis",
    items: [
      {
        name: "REST / OpenAPI",
        description: "HTTP APIs & contracts",
        descriptionKey: "techStack.items.openapi",
        iconUrl: "https://cdn.simpleicons.org/openapiinitiative/6BA539",
      },
      {
        name: "GraphQL",
        description: "Schema-first APIs",
        descriptionKey: "techStack.items.graphql",
        iconUrl: "https://cdn.simpleicons.org/graphql/E10098",
      },
      {
        name: "MQTT",
        description: "Realtime messaging",
        descriptionKey: "techStack.items.mqtt",
        iconUrl: "https://cdn.simpleicons.org/mqtt/660066",
      },
    ],
  },
  {
    titleKey: "techStack.backend",
    items: [
      {
        name: "Node.js",
        description: "APIs & server logic",
        descriptionKey: "techStack.items.node",
        iconUrl: "https://cdn.simpleicons.org/nodedotjs/5FA04E",
      },
      {
        name: "Keycloak",
        description: "Identity & access management",
        descriptionKey: "techStack.items.keycloak",
        iconUrl: "https://cdn.simpleicons.org/keycloak/2C2255",
      },
      {
        name: "GraphQL",
        description: "Schema-first APIs",
        descriptionKey: "techStack.items.graphql",
        iconUrl: "https://cdn.simpleicons.org/graphql/E10098",
      },
      {
        name: "PostgreSQL",
        description: "Relational data",
        descriptionKey: "techStack.items.postgresql",
        iconUrl: "https://cdn.simpleicons.org/postgresql/4169E1",
      },
      {
        name: "Prisma ORM",
        description: "Typed database layer",
        descriptionKey: "techStack.items.prisma",
        iconUrl: "https://cdn.simpleicons.org/prisma/2D3748",
      },
      {
        name: "Stripe",
        description: "Payments & billing",
        descriptionKey: "techStack.items.stripe",
        iconUrl: "https://cdn.simpleicons.org/stripe/635BFF",
      },
    ],
  },
  {
    titleKey: "techStack.ai",
    items: [
      {
        name: "Agent Harnesses",
        description: "OpenAI Codex · Claude Code · OpenCode",
        descriptionKey: "techStack.items.agentHarnesses",
        iconUrl: "/icons/agent-harness.svg",
      },
      {
        name: "RAG",
        description: "Retrieval-augmented generation",
        descriptionKey: "techStack.items.rag",
        iconUrl: "/icons/rag.svg",
      },
      {
        name: "LLM API Integration",
        description: "LLM APIs & application integration",
        descriptionKey: "techStack.items.llmApiIntegration",
        iconUrl: "/icons/llm-api.svg",
      },
      {
        name: "Prompt & Context Engineering",
        description: "Prompt design & context management",
        descriptionKey: "techStack.items.promptContextEngineering",
        iconUrl: "/icons/prompt-context.svg",
      },
      {
        name: "Tool Calling / MCP",
        description: "Agent tools & MCP integrations",
        descriptionKey: "techStack.items.toolCallingMcp",
        iconUrl: "/icons/tool-calling.svg",
      },
    ],
  },
  {
    titleKey: "techStack.devops",
    items: [
      {
        name: "Docker",
        description: "Containers & portability",
        descriptionKey: "techStack.items.docker",
        iconUrl: "https://cdn.simpleicons.org/docker/2496ED",
      },
      {
        name: "GitLab CI",
        description: "CI/CD pipelines",
        descriptionKey: "techStack.items.gitlab",
        iconUrl: "https://cdn.simpleicons.org/gitlab/FC6D26",
      },
      {
        name: "Vercel",
        description: "Edge deploys",
        descriptionKey: "techStack.items.vercel",
        iconUrl: "https://cdn.simpleicons.org/vercel/000000",
      },
      {
        name: "Nginx",
        description: "Reverse proxy & TLS",
        descriptionKey: "techStack.items.nginx",
        iconUrl: "https://cdn.simpleicons.org/nginx/009639",
      },
      {
        name: "Linux VPS",
        description: "Self-hosted deployments",
        descriptionKey: "techStack.items.linux",
        iconUrl: "https://cdn.simpleicons.org/linux/000000",
      },
    ],
  },
  {
    titleKey: "techStack.design",
    items: [
      {
        name: "Figma",
        description: "UX/UI systems",
        descriptionKey: "techStack.items.figma",
        iconUrl: "https://cdn.simpleicons.org/figma/F24E1E",
      },
      {
        name: "Three.js",
        description: "3D & WebGL visuals",
        descriptionKey: "techStack.items.threejsDesign",
        iconUrl: "https://cdn.simpleicons.org/threedotjs/000000",
      },
      {
        name: "Adobe Photoshop",
        description: "Imaging & post-production",
        descriptionKey: "techStack.items.photoshop",
        iconUrl: "/icons/photoshop.svg",
      },
      {
        name: "Autodesk 3ds Max",
        description: "Modeling & animation",
        descriptionKey: "techStack.items.max3ds",
        iconUrl: "/icons/3dsmax.svg",
      },
    ],
  },
  {
    titleKey: "techStack.collaboration",
    items: [
      {
        name: "Jira",
        description: "Project management & issue tracking",
        descriptionKey: "techStack.items.jira",
        iconUrl: "https://cdn.simpleicons.org/jira/0052CC",
      },
      {
        name: "Confluence",
        description: "Documentation & knowledge sharing",
        descriptionKey: "techStack.items.confluence",
        iconUrl: "https://cdn.simpleicons.org/confluence/172B4D",
      },
      {
        name: "Miro",
        description: "Collaborative design & ideation",
        descriptionKey: "techStack.items.miro",
        iconUrl: "https://cdn.simpleicons.org/miro/050038",
      },
    ],
  },
];

export default function TechStackSection() {
  const { t } = useTranslation();

  return (
    <section className="space-y-5">
      <div className="rounded-xl border border-black/20 bg-white p-6 dark:border-white/25 dark:bg-neutral-950">
        <p className="font-display mb-2 text-sm uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
          Capability Matrix
        </p>
        <h2 className="font-display text-3xl tracking-[-0.04em] md:text-5xl">
          {t("techStack.title")}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-neutral-600 md:text-base dark:text-neutral-300">
          {t("techStack.subtitle")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {stacks.map((stack, stackIndex) => (
          <motion.div
            key={stack.titleKey}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ delay: stackIndex * 0.1, duration: 0.4 }}
            className="group relative overflow-hidden rounded-xl border border-black/20 bg-white p-4 dark:border-white/25 dark:bg-neutral-950"
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-green-600/0 transition-all duration-300 group-hover:bg-green-600" />

            <h3 className="font-display relative mb-4 text-lg uppercase tracking-[0.08em]">
              {t(stack.titleKey as never)}
            </h3>

            <div className="relative space-y-2">
              {stack.items.map((item) => (
                <article
                  key={item.name}
                  className="rounded-lg border border-black/15 bg-white p-3 transition-colors duration-200 hover:border-green-600/60 dark:border-white/20 dark:bg-neutral-950 dark:hover:border-green-500/60"
                >
                  <div className="flex items-center gap-2">
                    <Image
                      src={item.iconUrl}
                      alt={`${item.name} icon`}
                      width={16}
                      height={16}
                      unoptimized
                    />
                    <h4 className="font-display text-sm uppercase tracking-[0.06em]">
                      {item.name}
                    </h4>
                  </div>
                  <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                    {item.descriptionKey
                      ? t(item.descriptionKey, {
                          defaultValue: item.description,
                        })
                      : item.description}
                  </p>
                </article>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
